import React, { useState, ReactNode } from 'react';

export interface TooltipProps {
  /** The tooltip content to display */
  content?: string | ReactNode;
  /** Alternative prop name for content (for backward compatibility) */
  text?: string | ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Tooltip position */
  position?: 'top' | 'right' | 'bottom' | 'left';
  /** Tooltip width (default: auto) */
  width?: string;
  /** Delay before showing tooltip (ms) */
  delay?: number;
  /** Optional class name for custom styling */
  className?: string;
}

/**
 * Tooltip - A standardized component for displaying tooltips across the application
 * 
 * This component ensures consistent tooltip behavior and styling.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  text,
  children,
  position = 'top',
  width = 'auto',
  delay = 300,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  
  // Use content or text prop (for backward compatibility)
  const tooltipContent = content || text;
  
  if (!tooltipContent) {
    return <>{children}</>;
  }

  const handleMouseEnter = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  // Position classes
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  };

  // Arrow classes
  const arrowClasses = {
    top: 'bottom-[-6px] left-1/2 -translate-x-1/2 border-t-gray-800 border-l-transparent border-r-transparent border-b-transparent',
    right: 'left-[-6px] top-1/2 -translate-y-1/2 border-r-gray-800 border-t-transparent border-b-transparent border-l-transparent',
    bottom: 'top-[-6px] left-1/2 -translate-x-1/2 border-b-gray-800 border-l-transparent border-r-transparent border-t-transparent',
    left: 'right-[-6px] top-1/2 -translate-y-1/2 border-l-gray-800 border-t-transparent border-b-transparent border-r-transparent',
  };

  return (
    <div 
      className="relative inline-block" 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {isVisible && (
        <div 
          className={`
            absolute z-[9999] px-3 py-2 text-sm text-white bg-gray-800 
            rounded shadow-lg whitespace-normal break-words
            ${positionClasses[position]}
            ${className}
          `}
          style={{ width }}
        >
          {tooltipContent}
          <div 
            className={`
              absolute w-0 h-0 
              border-solid border-4
              ${arrowClasses[position]}
            `}
          />
        </div>
      )}
    </div>
  );
};

export default Tooltip; 